import { useCallback, useMemo, useState } from "react";
import { Typography, Box, Skeleton, FormHelperText } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useField } from "formik";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import clsx from "clsx";

import defaultPlaceholderImage from "../assets/image-loading.gif";

const useStyles = makeStyles(() => ({
  label: { fontSize: 14, fontWeight: 600 },
  textField: {
    "&> *": {
      borderRadius: 6,
    },
  },
  skeleton: {
    height: 93,
    borderRadius: 4,
    marginTop: -10,
  },
  dragActive: {
    opacity: 0.3,
  },
  image: {
    objectFit: "contain",
    width: "100%",
    borderRadius: 6,
    maxHeight: 80,
  },
  dropBoxContainer: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #cbcbcb",
    borderRadius: 6,
    cursor: "pointer",
    padding: 12,
    "& p": {
      textAlign: "center",
    },
  },
  dragBox: {
    display: "flex",
    width: "100%",
    minHeight: 80,
    textAlign: "center",
    alignItems: "center",
  },
  activeDragBox: {
    border: "1px dashed #cbcbcb",
  },
  dragBoxText: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "16px",
    letterSpacing: "0.6px",
    textAlign: "center",
    marginTop: 6,
  },
  helperText: {
    marginLeft: 14,
  },
}));

export default function ImageField({ loading, name, label, required, onChange, prevImage, ...rest }) {
  const classes = useStyles();
  const [field, meta, helpers] = useField(name);
  const [error, setError] = useState("");

  const defaultImage = useMemo(() => prevImage || defaultPlaceholderImage, [prevImage]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      try {
        setError("");
        const reader = new FileReader();
        reader.readAsDataURL(acceptedFiles[0]);
        reader.onload = () => {
          if (typeof onChange === "function") {
            onChange(reader);
          }
          helpers.setValue(reader.result);
        };
        reader.onerror = () => {
          setError("An error has occured, please try again or contact SVA");
        };
      } catch (err) {
        setError("An error has occured, please try again or contact SVA");
        helpers.setValue(null);
      }
    },
    [helpers, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: "image/jpeg, image/png", maxFiles: 1 });

  // const handleImageRemove = useCallback(async () => {
  //   if (image) {
  //     setImage(undefined);
  //     setValue('image_base64', false);
  //   } else {
  //     setOpen(true);
  //   }
  // }, [image, setValue]);

  const imageMemo = useMemo(() => {
    const src = field.value || defaultImage;
    return (
      <img
        src={src}
        alt={name}
        className={clsx({
          [classes.image]: true,
          [classes.dragActive]: isDragActive,
        })}
        onError={() => {
          setError("Image failed to load");
          helpers.setValue(null);
        }}
      />
    );
  }, [defaultImage, helpers, classes, isDragActive, name, field]);

  return (
    <Box>
      <Typography color={(meta.touched && meta.error) || rest.error ? "error" : "text.primary"} gutterBottom className={classes.label}>
        {label}
        {required && "*"}
      </Typography>
      {loading ? (
        <Skeleton animation={false} className={classes.skeleton} />
      ) : (
        <div
          className={clsx({
            [classes.dropBoxContainer]: true,
            [classes.activeDragBox]: isDragActive,
          })}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {imageMemo}
          <Typography className={classes.dragBoxText}>Drop a .png or .jpeg image here, or click to select an image</Typography>
          {error && <Typography color="error">{error}</Typography>}
        </div>
      )}
      <FormHelperText error className={classes.helperText}>
        {(meta.touched && meta.error) || ""}
      </FormHelperText>
    </Box>
  );
}

ImageField.defaultProps = {
  loading: false,
  name: "",
  label: "",
  required: false,
  onChange: undefined,
  prevImage: null,
};

ImageField.propTypes = {
  loading: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  prevImage: PropTypes.string,
};
